import { Form, Spin, Splitter } from "antd"
import { isEmpty, keyBy, orderBy, uniq } from "lodash"
import { type ReactNode, useMemo } from "react"
import styled from "styled-components"

import { classAtoms, classSessionAtoms, makeupAtoms, tryoutAtoms } from "#atoms"
import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import Flex from "#blocks/Flex"
import { SFormHeader } from "#blocks/Form/formComponents"
import List from "#blocks/List"
import TextOverrun from "#blocks/TextOverrun"
import { NULL_ID } from "#classes/Firebase"
import { Tryout } from "#classes/Tryout"
import AttendanceIcon from "#components/AttendanceIcon"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  MakeupConstructor,
  MakeupForTable,
} from "#customTypes/composedTypes/makeupTypes"
import { AddStudentFormValues } from "#customTypes/composedTypes/studentTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { Attendance, AttendanceStatus } from "#customTypes/types"
import { useIsFulfilled, useTimeout } from "#hooks/jotai/useCascadingData"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import {
  useFilteredDocs,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"
import { COLORS } from "#styles/colors"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { formatISOStringWithTime } from "#utils/dateHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import { camelToWords } from "#utils/textFormatters"

type TryoutForDisplay = {
  id: Uuid
  studentName: string
  semester: string
  className: string
  startDate: string
  startTime: string
  endTime: string
  progress: ReactNode
  status: AttendanceStatus
}

const MAKEUP_ITEM_WIDTH = 200
const EMPTY_OBJECT = {}

const useGetStudentMakeupsAndTryouts = (studentId: Uuid | undefined) => {
  const student = useGetOneStudent(studentId)
  const attendance: Attendance = student?.attendance ?? EMPTY_OBJECT

  const makeupQueryConfig: FirebaseQueryConfig = useMemo(() => {
    return {
      queries: {
        studentId: {
          value: studentId ?? NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }
  }, [studentId])

  const makeups = useFilteredDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    makeupQueryConfig
  )

  const tryouts = useFilteredDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    makeupQueryConfig
  )

  const sessionIds = useMemo(() => {
    return uniq([
      ...(makeups?.map(({ missedSessionId }) => missedSessionId) ?? []),
      ...(makeups?.map(({ makeupSessionId }) => makeupSessionId) ?? []),
      ...(tryouts?.map(({ sessionId }) => sessionId) ?? []),
    ])
      .filter(Boolean)
      .sort() as Uuid[]
  }, [makeups, tryouts])

  const stableSessionIds = useStableIds(sessionIds)

  const classIds = useMemo(() => {
    return uniq(
      stableSessionIds.map((sessionId) => {
        const [classId] = getSessionInfoFromId(sessionId)
        return classId
      })
    ).filter(Boolean) as Uuid[]
  }, [stableSessionIds])

  const stableClassIds = useStableIds(classIds)

  const classes = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    stableClassIds
  )

  const sessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableSessionIds
  )

  const isMakeupsAndTryoutsFulfilled = useTimeout(
    3000,
    (makeups?.length ?? 0) > 0 || (tryouts?.length ?? 0) > 0
  )
  const isSessionsFulfilled = useIsFulfilled(stableSessionIds, sessions)
  const isClassesFulfilled = useIsFulfilled(stableClassIds, classes)

  const sessionsById = useMemo(() => {
    return keyBy(sessions, "id")
  }, [sessions])

  const classesById = useMemo(() => {
    return keyBy(classes, "id")
  }, [classes])

  // Build makeups for display
  const makeupsForDisplay: (Omit<MakeupForTable, "studentName"> & {
    status: AttendanceStatus
    id: Uuid
  })[] = useMemo(() => {
    if (!makeups || !sessions) return []

    return orderBy(makeups, "lastUpdatedAt", "desc").map((makeup) => {
      const [classId, sessionIndex] = getSessionInfoFromId(
        makeup.missedSessionId
      )

      return {
        id: makeup.id,
        reason: camelToWords(makeup.reason),
        missedClassName: classesById[makeup.missedClassId]?.name ?? "Unknown",
        missedClassSessionDate: formatISOStringWithTime(
          sessionsById[makeup.missedSessionId]?.eventStart
        ),
        makeupClassName: makeup.makeupClassId
          ? classesById[makeup.makeupClassId]?.name
          : "Not scheduled",
        makeupClassSessionDate: makeup.makeupSessionId
          ? formatISOStringWithTime(
              sessionsById[makeup.makeupSessionId]?.eventStart
            )
          : "Not scheduled",
        createdAt: formatISOStringWithTime(makeup.createdAt),
        semester: formatSemester(makeup.semester),
        status:
          attendance[classId]?.[sessionIndex]?.status ??
          ATTENDANCE_STATUSES.INVALID,
      }
    })
  }, [makeups, sessions, classesById, sessionsById, attendance])

  // Build tryouts for display
  const tryoutsForDisplay: TryoutForDisplay[] = useMemo(() => {
    if (!tryouts || !student || isEmpty(classesById) || isEmpty(sessionsById))
      return []

    return orderBy(
      tryouts,
      ["semester", "lastUpdatedAt"],
      ["desc", "desc"]
    ).map((tryout) => {
      const tryoutInstance = new Tryout(tryout)
      const [classId, sessionIndex] = getSessionInfoFromId(tryout.sessionId)
      const classObj = classesById[classId]
      const classSessionObj = sessionsById[tryout.sessionId]

      const hasAllData = !!(student && classObj && classSessionObj)

      const tryoutForTable = hasAllData
        ? tryoutInstance.getTryoutForTable(student, classObj, classSessionObj)
        : {
            id: tryout.id,
            studentName: "Unknown student",
            semester: formatSemester(tryout.semester),
            className: "Unknown class",
            startDate: "Unknown",
            startTime: "Unknown",
            endTime: "Unknown",
            progress: tryoutInstance.progress,
          }

      return {
        ...tryoutForTable,
        status:
          attendance[classId]?.[sessionIndex]?.status ??
          ATTENDANCE_STATUSES.INVALID,
      }
    })
  }, [tryouts, student, classesById, sessionsById, attendance])

  if (!studentId || !student) return { makeups: [], tryouts: [] }

  return {
    makeups: makeupsForDisplay,
    tryouts: tryoutsForDisplay,
    isFulfilled:
      isMakeupsAndTryoutsFulfilled && isSessionsFulfilled && isClassesFulfilled,
  }
}

const SSubtitle = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: ${COLORS.GRAY7};
`

const MakeupItemContent = ({
  item,
}: {
  item: Omit<MakeupForTable, "studentName"> & {
    status: AttendanceStatus
    id: Uuid
  }
}) => {
  const {
    missedClassName,
    missedClassSessionDate,
    makeupClassName,
    makeupClassSessionDate,
  } = item

  const missedClass = `${missedClassName}`
  const makeupClass = `${makeupClassName}`

  return (
    <Flex gap={30} style={{ width: "100%" }}>
      <Flex center>
        <AttendanceIcon status={item.status} />
      </Flex>

      <Flex col style={{ width: MAKEUP_ITEM_WIDTH }}>
        <h4 style={{ width: MAKEUP_ITEM_WIDTH }}>
          <TextOverrun text={missedClass} />
        </h4>
        <SSubtitle>{missedClassSessionDate}</SSubtitle>
      </Flex>

      <Flex col>
        <h4>{makeupClass}</h4>
        <SSubtitle>{makeupClassSessionDate}</SSubtitle>
      </Flex>
    </Flex>
  )
}

const MakeupItem = ({
  item,
}: {
  item: Omit<MakeupForTable, "studentName"> & {
    status: AttendanceStatus
    id: Uuid
  }
}) => (
  <ComponentErrorBoundary
    variant="line"
    context={`Makeup: ${item.missedClassName}`}
  >
    <MakeupItemContent item={item} />
  </ComponentErrorBoundary>
)

const TryoutItemContent = ({ item }: { item: TryoutForDisplay }) => {
  return (
    <Flex gap={30} style={{ width: "100%" }}>
      <Flex center>
        <AttendanceIcon status={item.status} />
      </Flex>
      <Flex col grow>
        <Flex gap={8}>
          <h4>{item.className}</h4>
          {item.progress}
        </Flex>
        <SSubtitle>{item.startDate}</SSubtitle>
      </Flex>
    </Flex>
  )
}

const TryoutItem = ({ item }: { item: TryoutForDisplay }) => (
  <ComponentErrorBoundary variant="line" context={`Tryout: ${item.className}`}>
    <TryoutItemContent item={item} />
  </ComponentErrorBoundary>
)

const StudentMakeupsList = () => {
  const form = Form.useFormInstance<AddStudentFormValues>()
  const studentId = Form.useWatch("id", form)

  const { makeups, tryouts, isFulfilled } =
    useGetStudentMakeupsAndTryouts(studentId)

  const MAX_HEIGHT = 700

  return (
    <Flex col gap={10} style={{ height: MAX_HEIGHT + 100 }}>
      <Splitter layout="vertical" style={{ height: "100%" }}>
        <Splitter.Panel
          style={{ paddingBottom: 20 }}
          max={MAX_HEIGHT}
          defaultSize={150}
        >
          <Flex col gap={4} style={{ height: "100%" }}>
            <SFormHeader>Tryouts</SFormHeader>
            {!isFulfilled ? (
              <Flex>
                <Spin />
              </Flex>
            ) : isFulfilled && !tryouts.length ? (
              <Flex>No tryouts found</Flex>
            ) : (
              <List
                items={tryouts}
                component={TryoutItem}
                style={{ maxHeight: MAX_HEIGHT }}
              />
            )}
          </Flex>
        </Splitter.Panel>

        <Splitter.Panel style={{ paddingTop: 20 }} max={MAX_HEIGHT}>
          <Flex col gap={4} style={{ height: "100%" }}>
            <SFormHeader>Makeups</SFormHeader>

            {!isFulfilled ? (
              <Flex>
                <Spin />
              </Flex>
            ) : isFulfilled && !makeups.length ? (
              <Flex>No makeups found</Flex>
            ) : (
              <>
                <Flex gap={30} style={{ marginBottom: 10 }}>
                  <h3 style={{ width: MAKEUP_ITEM_WIDTH + 52 }}>
                    Missed Class
                  </h3>
                  <h3>Makeup Class</h3>
                </Flex>
                <List
                  items={makeups}
                  layout="vertical"
                  component={MakeupItem}
                  gap={12}
                  style={{ maxHeight: MAX_HEIGHT, paddingBottom: 30 }}
                />
              </>
            )}
          </Flex>
        </Splitter.Panel>
      </Splitter>
    </Flex>
  )
}

export default StudentMakeupsList
